import { Container } from '@mui/material';
import { Paths, ProjectPathParams } from '../../app/router/app.router';
import { useQueryGetSettingsTree } from './settings.queries';
import { ErrorFullPage } from '../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { LoadingIndicator } from '../../ui/components/pure/loading-indicator/loading-indicator.component';
import { WindowTitle } from '../../core/window-title/window-title.component';
import { useQueryGetProject } from '../../core/queries/projects.queries';
import { ListPageHeader } from '../../ui/components/pure/page-headers/list/list-page-header.component';
import { SettingsPanels } from './components/settings-panels.component';
import { SettingsTreeReducerProvider } from './reducer/settings-tree-reducer.context';
import { SettingsTreeActionsObserver } from './settings-page.actions-observer';
import { SettingsTreeUrlSync } from './deep-link/settings-tree-url-sync.component';
import { useMemo } from 'react';
import {
  EnrichedSettingsTreeDto,
  normalizeSettingsNodeStyle,
  SettingsTreeDataRequestType
} from './reducer/settings-tree-reducer.types';
import { useHasUIFeatures } from '../../core/permissions/has-permissions.hook';

export function SettingsPage({ params }: { params: ProjectPathParams<Paths.settings> }) {
  const settingsTreeResult = useQueryGetSettingsTree(params.projectID);
  const enrichedSettingsTree: EnrichedSettingsTreeDto = useMemo(() => {
    return {
      nodes: (settingsTreeResult.data?.nodes || []).map((node) => ({
        ...node,
        style: normalizeSettingsNodeStyle(node.style),
        isPlaceholder: false,
        request: {
          type: SettingsTreeDataRequestType.Projects,
          rootId: params.projectID
        }
      }))
    };
  }, [params.projectID, settingsTreeResult.data?.nodes]);

  const projectResult = useQueryGetProject(params.projectID);

  const uiFeatureCheck = useHasUIFeatures(['internal-groundfloor']);

  if (!uiFeatureCheck.hasAllFeatures) {
    return <ErrorFullPage preset="GLOBAL_NOT_FOUND" />;
  }

  const error = settingsTreeResult.error || projectResult.error;

  if (error) {
    if (error.statusCode === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }

    if (error.statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" entity="project" />;
    }

    if (error.statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" entity="project" />;
    }

    return <ErrorFullPage />;
  }

  const isLoading = settingsTreeResult.isLoading || projectResult.isLoading || uiFeatureCheck.isLoading;

  if (isLoading) {
    return (
      <Container>
        <LoadingIndicator />
      </Container>
    );
  }

  if (settingsTreeResult.data === undefined || projectResult.data === undefined) {
    return <ErrorFullPage preset="NOT_FOUND" entity={'project'} />;
  }

  return (
    <>
      <WindowTitle detail={projectResult.data.name} pageTitle="Settings" />
      <ListPageHeader title={projectResult.data.name} subtitle="Edit Project" />
      <SettingsTreeReducerProvider settingsTreeDto={enrichedSettingsTree}>
        <SettingsPanels />
        <SettingsTreeActionsObserver />
        <SettingsTreeUrlSync />
      </SettingsTreeReducerProvider>
    </>
  );
}
